import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { sb } from '../../lib/supabase';
import { useSupabaseAuth } from '../../context/AuthContext';
import useWorkspaceAccess from '../../hooks/useWorkspaceAccess';
import Loader from '../../components/Loader';
import InitiationLayout from '../../components/InitiationLayout';
import {
  ROM_TABS,
  ROM_STATUS_LABEL,
  OOTB_CLASSIFICATION_LABEL,
  REQUIREMENT_TYPE_LABEL,
  statusBadgeClass,
} from '../../lib/initiation';
import {
  calcROM,
  calcReqEffort,
  calcIntEffort,
  overridesFromRows,
  buildRomNote,
  isOverrideKey,
  defaultOverrideValue,
  WS_LABELS,
  WS_COLORS,
  COST_LENS_LABEL,
} from '../../lib/rom';

function fmtMoney(val, currency) {
  if (val == null) return '—';
  const sym = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : currency === 'USD' ? '$' : '';
  if (val >= 10000) return `${sym}${Math.round(val / 1000)}k`;
  return `${sym}${Math.round(val).toLocaleString()}`;
}

function fmtH(n) {
  return `${Math.round(n).toLocaleString()}h`;
}

function ootbBadgeStyle(ootb) {
  const map = {
    ootb: { bg: '#e1f5ee', color: '#085041', border: '#5dcaa5' },
    config: { bg: '#EEEDFE', color: '#3C3489', border: '#AFA9EC' },
    enhancement: { bg: '#faeeda', color: '#633806', border: '#fac775' },
    custom_dev: { bg: '#FCEBEB', color: '#A32D2D', border: '#F5ACAB' },
  };
  return map[ootb] || map.config;
}

function TabNav({ active, onChange }) {
  return (
    <div className="rom-tab-nav">
      {ROM_TABS.map((t) => (
        <button
          key={t.key}
          type="button"
          className={`rom-tab-item${active === t.key ? ' on' : ''}`}
          onClick={() => onChange(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export default function RomEstimate() {
  const { project, programme } = useOutletContext();
  const { profile } = useSupabaseAuth();
  const { canWrite } = useWorkspaceAccess();

  const [businessCase, setBusinessCase] = useState(null);
  const [charter, setCharter] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [interfaces, setInterfaces] = useState([]);
  const [overrideRows, setOverrideRows] = useState([]);
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [openSections, setOpenSections] = useState({ extrap: true, risk: true, cost: true });
  const [overrideModal, setOverrideModal] = useState(null);
  const [overrideDraft, setOverrideDraft] = useState({ value: '', reason: '' });

  const locked = businessCase?.status === 'review' || businessCase?.status === 'approved';
  const readOnly = !canWrite || locked;
  const overrides = useMemo(() => overridesFromRows(overrideRows), [overrideRows]);
  const overrideLog = useMemo(
    () => (Array.isArray(businessCase?.breakdown_json?.override_log) ? businessCase.breakdown_json.override_log : []),
    [businessCase?.breakdown_json]
  );

  const calc = useMemo(
    () => calcROM({ requirements, interfaces, overrides }),
    [requirements, interfaces, overrides]
  );

  const currency = businessCase?.cost_currency || charter?.budget_currency || 'GBP';
  const envelope = charter?.budget_envelope != null ? Number(charter.budget_envelope) : null;
  const envelopePct = envelope ? Math.round((calc.expected / envelope) * 100) : null;
  const barColor = envelopePct == null ? 'ok' : envelopePct > 100 ? 'over' : envelopePct > 90 ? 'warn' : 'ok';

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const { data: selectedOption } = await sb
        .from('architecture_options')
        .select('id, name')
        .eq('project_id', project.id)
        .eq('status', 'selected')
        .maybeSingle();

      const bcQuery = sb.from('business_cases').select('*').eq('project_id', project.id);
      if (selectedOption?.id) {
        bcQuery.eq('architecture_option_id', selectedOption.id);
      }
      const [bcRes, chRes, reqRes, intRes, ovRes, assessRes] = await Promise.all([
        bcQuery.order('version_number', { ascending: false }).limit(1).maybeSingle(),
        programme?.id
          ? sb.from('programme_charters').select('budget_envelope, budget_currency').eq('programme_id', programme.id).is('project_id', null).maybeSingle()
          : Promise.resolve({ data: null }),
        sb.from('requirements')
          .select('id, ref, title, ootb_classification, t_shirt_size, requirement_type, status')
          .eq('project_id', project.id)
          .in('status', ['sized', 'approved', 'baselined']),
        selectedOption?.id
          ? sb.from('interfaces').select('id, ref, name, interface_type, t_shirt_size, estimated_build_hours').eq('architecture_option_id', selectedOption.id)
          : sb.from('interfaces').select('id, ref, name, interface_type, t_shirt_size, estimated_build_hours').eq('project_id', project.id),
        programme?.id
          ? sb.from('programme_estimation_overrides').select('*').eq('programme_id', programme.id)
          : Promise.resolve({ data: [] }),
        selectedOption?.id
          ? sb.from('requirement_option_assessments').select('*').eq('architecture_option_id', selectedOption.id)
          : Promise.resolve({ data: [] }),
      ]);

      const assessMap = new Map((assessRes.data || []).map((a) => [a.requirement_id, a]));
      const reqs = (reqRes.data || []).map((r) => {
        const a = assessMap.get(r.id);
        return {
          ...r,
          ootb_classification: a?.ootb_classification ?? r.ootb_classification,
          t_shirt_size: a?.t_shirt_size ?? r.t_shirt_size,
        };
      }).filter((r) => r.t_shirt_size);

      setBusinessCase(bcRes.data || null);
      setCharter(chRes.data || null);
      setRequirements(reqs);
      setInterfaces(intRes.data || []);
      setOverrideRows(ovRes.data || []);
    } catch (e) {
      console.error('[RomEstimate] load failed', e);
      showToast('Failed to load ROM estimate');
    } finally {
      setLoading(false);
    }
  }, [project?.id, programme?.id]);

  useEffect(() => { load(); }, [load]);

  async function ensureBusinessCase() {
    if (businessCase?.id) return businessCase;
    const { data, error } = await sb
      .from('business_cases')
      .insert({
        programme_id: programme?.id || null,
        project_id: project.id,
        version_number: 1,
        version_name: 'ROM v1',
        status: 'draft',
        cost_currency: charter?.budget_currency || 'GBP',
        created_by: profile?.id || null,
      })
      .select()
      .single();
    if (error) throw error;
    setBusinessCase(data);
    return data;
  }

  async function saveCalculation(extraLog) {
    setSaving(true);
    try {
      const bc = await ensureBusinessCase();
      const romNote = buildRomNote(calc, envelope, currency);
      const breakdown = {
        workstreams: calc.ws,
        cost_lens: calc.costLens,
        override_log: extraLog || overrideLog,
        inputs: {
          requirement_count: calc.requirementCount,
          interface_count: calc.interfaceCount,
        },
      };
      const { error } = await sb
        .from('business_cases')
        .update({
          total_build_hours: calc.totalBuildHrs,
          risk_adjusted_hours: calc.riskAdjHrs,
          risk_factor: calc.riskFactor,
          hours_per_day: calc.hrsPerDay,
          blended_day_rate: calc.dayRate,
          contingency_percentage: calc.contingencyPct,
          likely_consultant_days: calc.likelyDays,
          optimistic_cost: calc.optimistic,
          likely_cost: calc.likely,
          pessimistic_cost: calc.pessimistic,
          expected_cost: calc.expected,
          cost_currency: currency,
          breakdown_json: breakdown,
          rom_note: romNote,
          calculated_at: new Date().toISOString(),
          calculated_by: profile?.id || null,
          status: bc.status === 'empty' ? 'draft' : bc.status,
        })
        .eq('id', bc.id);
      if (error) throw error;
      setBusinessCase((b) => ({
        ...b,
        ...{
          total_build_hours: calc.totalBuildHrs,
          risk_adjusted_hours: calc.riskAdjHrs,
          risk_factor: calc.riskFactor,
          expected_cost: calc.expected,
          optimistic_cost: calc.optimistic,
          likely_cost: calc.likely,
          pessimistic_cost: calc.pessimistic,
          breakdown_json: breakdown,
          rom_note: romNote,
          calculated_at: new Date().toISOString(),
          status: 'draft',
        },
      }));
      showToast('ROM calculated and saved', 'success');
    } catch (e) {
      showToast(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function submitForReview() {
    if (!businessCase?.id || readOnly) return;
    await saveCalculation();
    setSaving(true);
    try {
      const { error } = await sb.from('business_cases').update({ status: 'review' }).eq('id', businessCase.id);
      if (error) throw error;
      setBusinessCase((b) => ({ ...b, status: 'review' }));
      showToast('Submitted for approval', 'success');
    } catch (e) {
      showToast(e.message || 'Submit failed');
    } finally {
      setSaving(false);
    }
  }

  async function approveRom() {
    if (!businessCase?.id) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const { error } = await sb
        .from('business_cases')
        .update({ status: 'approved', approved_at: now, approved_by: profile?.id || null })
        .eq('id', businessCase.id);
      if (error) throw error;
      setBusinessCase((b) => ({ ...b, status: 'approved', approved_at: now }));
      showToast('ROM estimate approved', 'success');
    } catch (e) {
      showToast(e.message || 'Approval failed');
    } finally {
      setSaving(false);
    }
  }

  async function applyOverride() {
    if (!overrideModal || !programme?.id) return;
    const val = parseFloat(overrideDraft.value);
    const reason = overrideDraft.reason.trim();
    if (Number.isNaN(val) || !reason) {
      showToast('Enter a valid value and reason');
      return;
    }
    setSaving(true);
    try {
      const valueType = overrideModal.key.startsWith('extrap_')
        ? 'percentage'
        : overrideModal.key.startsWith('risk_')
          ? 'multiplier'
          : overrideModal.key === 'dayRate'
            ? 'currency_rate'
            : overrideModal.key === 'hrsPerDay'
              ? 'hours_per_day'
              : 'percentage';

      const { error } = await sb.from('programme_estimation_overrides').upsert(
        {
          programme_id: programme.id,
          factor_key: overrideModal.key,
          value_numeric: val,
          value_type: valueType,
          note: reason,
          set_by: profile?.id || null,
        },
        { onConflict: 'programme_id,factor_key' }
      );
      if (error) throw error;

      const logEntry = {
        field: overrideModal.key,
        label: overrideModal.label,
        old_val: overrideModal.current,
        new_val: val,
        reason,
        at: new Date().toISOString(),
        by: profile?.full_name || profile?.email || 'User',
      };
      const nextLog = [...overrideLog.filter((l) => l.field !== overrideModal.key), logEntry];

      const { data: rows } = await sb
        .from('programme_estimation_overrides')
        .select('*')
        .eq('programme_id', programme.id);
      setOverrideRows(rows || []);

      if (businessCase?.id) {
        const breakdown = { ...(businessCase.breakdown_json || {}), override_log: nextLog };
        await sb.from('business_cases').update({ breakdown_json: breakdown }).eq('id', businessCase.id);
        setBusinessCase((b) => ({ ...b, breakdown_json: breakdown }));
      }

      setOverrideModal(null);
      setOverrideDraft({ value: '', reason: '' });
      showToast('Override applied', 'success');
    } catch (e) {
      showToast(e.message || 'Override failed');
    } finally {
      setSaving(false);
    }
  }

  async function resetOverride(key) {
    if (!programme?.id) return;
    setSaving(true);
    try {
      await sb.from('programme_estimation_overrides').delete().eq('programme_id', programme.id).eq('factor_key', key);
      const { data: rows } = await sb
        .from('programme_estimation_overrides')
        .select('*')
        .eq('programme_id', programme.id);
      setOverrideRows(rows || []);
      if (businessCase?.id) {
        const nextLog = overrideLog.filter((l) => l.field !== key);
        const breakdown = { ...(businessCase.breakdown_json || {}), override_log: nextLog };
        await sb.from('business_cases').update({ breakdown_json: breakdown }).eq('id', businessCase.id);
        setBusinessCase((b) => ({ ...b, breakdown_json: breakdown }));
      }
      showToast('Reset to default', 'success');
    } catch (e) {
      showToast(e.message || 'Reset failed');
    } finally {
      setSaving(false);
    }
  }

  const assumptionSections = [
    {
      id: 'extrap',
      title: 'Extrapolation factors (% of build effort)',
      rows: [
        { label: 'Solution design & blueprinting', key: 'extrap_design', fmt: (v) => `${v}%`, defaultFmt: '30%' },
        { label: 'Testing — SIT, UAT, regression', key: 'extrap_test', fmt: (v) => `${v}%`, defaultFmt: '35%' },
        { label: 'Data migration', key: 'extrap_migration', fmt: (v) => `${v}%`, defaultFmt: '20%' },
        { label: 'Cutover planning & execution', key: 'extrap_cutover', fmt: (v) => `${v}%`, defaultFmt: '10%' },
        { label: 'Training & change management', key: 'extrap_training', fmt: (v) => `${v}%`, defaultFmt: '18%' },
        { label: 'Programme management', key: 'extrap_pm', fmt: (v) => `${v}%`, defaultFmt: '15%' },
        { label: 'Post go-live support', key: 'extrap_hypercare', fmt: (v) => `${v}%`, defaultFmt: '10%' },
      ],
    },
    {
      id: 'risk',
      title: 'Risk & adjustment factors (multiplicative)',
      rows: [
        { label: 'Requirements completeness', key: 'risk_completeness', fmt: (v) => `${v}×`, defaultFmt: '1.0×', source: 'SA assessment' },
        { label: 'Team experience on platform', key: 'risk_experience', fmt: (v) => `${v}×`, defaultFmt: '1.0×', source: 'PM assessment' },
        { label: 'Client organisational complexity', key: 'risk_complexity', fmt: (v) => `${v}×`, defaultFmt: '1.0×', source: 'PM assessment' },
        { label: 'Data quality risk', key: 'risk_dataQuality', fmt: (v) => `${v}×`, defaultFmt: '1.0×', source: 'SA assessment' },
        { label: 'Combined risk factor', key: null, fmt: () => `×${calc.riskFactor.toFixed(2)}`, defaultFmt: '×1.0', source: 'Product of all four factors' },
      ],
    },
    {
      id: 'cost',
      title: 'Cost parameters',
      rows: [
        { label: 'SI blended day rate', key: 'dayRate', fmt: (v) => `£${v}/day`, defaultFmt: '£920/day', source: 'Company default' },
        { label: 'Hours per consultant day', key: 'hrsPerDay', fmt: (v) => `${v}h`, defaultFmt: '8h', source: 'Company default' },
        { label: 'Contingency', key: 'contingency', fmt: (v) => `${v}%`, defaultFmt: '15%', source: 'Company default' },
      ],
    },
  ];

  if (loading) return <Loader text="Loading ROM estimate…" />;

  if (!project?.id) {
    return (
      <InitiationLayout title="ROM estimate" subtitle="IN-05">
        <div className="empty">Open a project to view the ROM estimate.</div>
      </InitiationLayout>
    );
  }

  const status = businessCase?.calculated_at ? (businessCase.status || 'draft') : 'empty';
  const badge = (
    <span className={`b ${statusBadgeClass(status === 'empty' ? '' : status)}`}>
      {ROM_STATUS_LABEL[status] || status}
    </span>
  );

  const costLensGroups = Object.entries(calc.costLens).filter(([, hrs]) => hrs > 0);

  return (
    <InitiationLayout
      title="ROM estimate"
      subtitle={`${project.name} · IN-05`}
      badge={badge}
      actions={
        !canWrite ? null : !locked ? (
          <>
            <button type="button" className="btn-ghost" disabled={saving} onClick={() => saveCalculation()}>
              Recalculate & save
            </button>
            {businessCase?.calculated_at && businessCase.status === 'draft' && (
              <button type="button" className="btn-primary" disabled={saving} onClick={submitForReview}>
                Submit for approval
              </button>
            )}
          </>
        ) : businessCase?.status === 'review' ? (
          <button type="button" className="btn-primary" disabled={saving} onClick={approveRom}>
            Approve ROM
          </button>
        ) : null
      }
    >
      <div className="rom-body">
        {businessCase?.status === 'approved' && (
          <div className="approval-banner">
            <span className="approval-banner-icon">✓</span>
            <div className="approval-banner-text">
              <strong>ROM estimate approved</strong>
              {businessCase.approved_at && ` · ${new Date(businessCase.approved_at).toLocaleDateString()}`}
              {businessCase.expected_cost != null && ` · PERT expected ${fmtMoney(businessCase.expected_cost, currency)}`}
            </div>
          </div>
        )}

        {requirements.length === 0 && (
          <div className="submit-banner" style={{ marginBottom: 12 }}>
            <span className="submit-banner-icon">!</span>
            <div>No sized requirements yet — complete IN-02 sizing before calculating ROM.</div>
          </div>
        )}

        <TabNav active={activeTab} onChange={setActiveTab} />

        {activeTab === 'summary' && (
          <div className="rom-tab-panel">
            <div className="pert-card">
              <div className="pert-top">
                <div>
                  <div className="pert-title">PERT cost estimate</div>
                  <div style={{ fontSize: 10.5, color: '#888780', marginTop: 2 }}>
                    Based on {calc.requirementCount} requirements + {calc.interfaceCount} interfaces · {calc.likelyDays} consultant days (most likely)
                  </div>
                </div>
                {envelope != null && (
                  <div className="pert-envelope">
                    <div className="pert-envelope-val">{fmtMoney(envelope, currency)}</div>
                    <div className="pert-envelope-label">Charter budget envelope</div>
                  </div>
                )}
              </div>
              <div className="pert-scenarios">
                {[
                  ['Optimistic (×0.85)', calc.optimistic, 'optimistic', 0.85],
                  ['Most likely (×1.0)', calc.likely, 'likely', 1],
                  ['Pessimistic (×1.25)', calc.pessimistic, 'pessimistic', 1.25],
                  ['PERT expected', calc.expected, 'expected', null],
                ].map(([label, val, cls, mult]) => (
                  <div key={label} className={`pert-scenario ${cls}`}>
                    <div className="pert-scenario-label">{label}</div>
                    <div className="pert-scenario-val">{fmtMoney(val, currency)}</div>
                    <div className="pert-scenario-days">
                      {mult != null && envelopePct != null
                        ? `${Math.round(calc.likelyDays * mult)}d · ${Math.round(envelopePct * mult)}% of envelope`
                        : '(O + 4M + P) / 6'}
                    </div>
                  </div>
                ))}
              </div>
              {envelope != null && envelopePct != null && (
                <div className="budget-bar-wrap">
                  <div className="budget-bar-track">
                    <div className={`budget-bar-fill ${barColor}`} style={{ width: `${Math.min(envelopePct, 100)}%` }} />
                  </div>
                  <div className="budget-bar-labels">
                    <span>{fmtMoney(0, currency)}</span>
                    <span style={{ fontWeight: 500, color: barColor === 'over' ? '#C23535' : barColor === 'warn' ? '#D4831A' : '#534AB7' }}>
                      {envelopePct}% of budget envelope ·{' '}
                      {calc.expected <= envelope
                        ? `${fmtMoney(envelope - calc.expected, currency)} headroom`
                        : `${fmtMoney(calc.expected - envelope, currency)} over budget`}
                    </span>
                    <span>{fmtMoney(envelope, currency)}</span>
                  </div>
                </div>
              )}
              {overrideRows.length > 0 && (
                <div className="rom-override-hint">
                  {overrideRows.length} programme override{overrideRows.length > 1 ? 's' : ''} applied — see Assumptions tab
                </div>
              )}
              <div className="pert-formula" style={{ marginTop: 8 }}>
                Risk factor: ×{calc.riskFactor.toFixed(2)} · Day rate: £{calc.dayRate}/day · Contingency: {Math.round(calc.contingency * 100)}% · {calc.hrsPerDay}h/day
              </div>
            </div>

            <div className="sa-card">
              <div className="sa-card-hdr">
                <span className="sa-card-title">Workstream breakdown (PERT most likely)</span>
              </div>
              <div className="sa-card-body">
                <div className="ws-grid">
                  {Object.entries(calc.ws).map(([k, h]) => (
                    <div key={k} className="ws-card">
                      <div className="ws-card-top">
                        <div className="ws-card-name">{WS_LABELS[k]}</div>
                        <div className="ws-card-hrs">{fmtMoney((h / calc.hrsPerDay) * calc.dayRate, currency)}</div>
                      </div>
                      <div className="ws-card-pct">
                        {fmtH(h)} · {calc.totalHrs ? Math.round((h / calc.totalHrs) * 100) : 0}% of total effort
                      </div>
                      <div className="ws-bar" style={{ marginTop: 5 }}>
                        <div
                          className="ws-bar-fill"
                          style={{ width: `${calc.totalHrs ? Math.round((h / calc.totalHrs) * 100) : 0}%`, background: WS_COLORS[k] }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {costLensGroups.length > 0 && (
              <div className="sa-card">
                <div className="sa-card-hdr">
                  <span className="sa-card-title">Three-level cost lens (build hours)</span>
                </div>
                <div className="sa-card-body">
                  <div className="ws-grid">
                    {costLensGroups.map(([key, hrs]) => (
                      <div key={key} className="ws-card">
                        <div className="ws-card-top">
                          <div className="ws-card-name">{COST_LENS_LABEL[key] || key}</div>
                          <div className="ws-card-hrs">{fmtH(hrs)}</div>
                        </div>
                        <div className="ws-card-pct">
                          {calc.totalBuildHrs ? Math.round((hrs / calc.totalBuildHrs) * 100) : 0}% of build effort
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="rom-note">
              <strong>ROM note:</strong>{' '}
              {businessCase?.rom_note || buildRomNote(calc, envelope, currency)}
            </div>
          </div>
        )}

        {activeTab === 'build' && (
          <div className="rom-tab-panel">
            <div className="sa-card">
              <div className="sa-card-hdr">
                <span className="sa-card-title">Build effort by requirement</span>
                <span style={{ fontSize: 11, color: '#888780' }}>
                  {requirements.length} requirements · hours = size × classification
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="build-table">
                  <thead>
                    <tr>
                      <th className="left">Requirement</th>
                      <th>Cost lens</th>
                      <th>Class</th>
                      <th>Size</th>
                      <th className="num">Total hrs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requirements.length === 0 ? (
                      <tr><td colSpan={5} style={{ color: '#888780', padding: 14 }}>No sized requirements</td></tr>
                    ) : (
                      <>
                        {requirements.map((r) => {
                          const hrs = calcReqEffort(r, overrides);
                          const ootbStyle = ootbBadgeStyle(r.ootb_classification);
                          return (
                            <tr key={r.id}>
                              <td>{r.ref || '—'} · <span className="muted">{r.title}</span></td>
                              <td className="muted">{REQUIREMENT_TYPE_LABEL[r.requirement_type] || r.requirement_type}</td>
                              <td>
                                <span
                                  className="rom-ootb-pill"
                                  style={{ background: ootbStyle.bg, color: ootbStyle.color, borderColor: ootbStyle.border }}
                                >
                                  {OOTB_CLASSIFICATION_LABEL[r.ootb_classification] || r.ootb_classification || '—'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center', fontWeight: 600 }}>{r.t_shirt_size}</td>
                              <td className="num">{hrs}h</td>
                            </tr>
                          );
                        })}
                        {interfaces.length > 0 && (
                          <>
                            <tr className="group-row">
                              <td colSpan={4}>Interfaces ({interfaces.length} external)</td>
                              <td className="num">{fmtH(calc.intBuildHrs)}</td>
                            </tr>
                            {interfaces.map((i) => {
                              const hrs = calcIntEffort(i);
                              return (
                                <tr key={i.id}>
                                  <td>{i.ref || '—'} · <span className="muted">{i.name}</span></td>
                                  <td className="muted">Interface</td>
                                  <td><span className="rom-ootb-pill" style={{ background: '#E6F1FB', color: '#0C447C', borderColor: '#85B7EB' }}>{i.interface_type}</span></td>
                                  <td style={{ textAlign: 'center', fontWeight: 600 }}>{i.t_shirt_size || 'M'}</td>
                                  <td className="num">{hrs}h</td>
                                </tr>
                              );
                            })}
                          </>
                        )}
                        <tr className="total-row">
                          <td colSpan={4}><strong>Total build effort</strong></td>
                          <td className="num"><strong>{fmtH(calc.totalBuildHrs)}</strong></td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'assumptions' && (
          <div className="rom-tab-panel">
            <div className="sa-card">
              <div className="sa-card-hdr">
                <span className="sa-card-title">Assumptions & overrides</span>
                <span style={{ fontSize: 11, color: '#888780' }}>Click any value to override · all changes logged</span>
              </div>
              <div className="assumptions-panel">
                {assumptionSections.map((sec) => {
                  const hasOverride = sec.rows.some((r) => r.key && isOverrideKey(r.key, overrides));
                  return (
                    <div key={sec.id} className="assumption-section">
                      <button
                        type="button"
                        className="assumption-section-hdr"
                        onClick={() => setOpenSections((s) => ({ ...s, [sec.id]: !s[sec.id] }))}
                      >
                        <div className="assumption-section-title">{sec.title}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {hasOverride && <span className="rom-override-badge">Overridden</span>}
                          <span style={{ fontSize: 12, color: '#888780' }}>{openSections[sec.id] ? '▾' : '▸'}</span>
                        </div>
                      </button>
                      {openSections[sec.id] && (
                        <div className="assumption-section-body open">
                          {sec.rows.map((row) => {
                            const overridden = row.key && isOverrideKey(row.key, overrides);
                            const val = row.key
                              ? row.fmt(overrides[row.key] ?? defaultOverrideValue(row.key))
                              : row.fmt();
                            const log = overrideLog.find((l) => l.field === row.key);
                            return (
                              <div key={row.label} className="assumption-row">
                                <div className="assumption-left">
                                  <div className="assumption-label">{row.label}</div>
                                  <div className="assumption-source">{row.source || 'Platform KB default'}</div>
                                  {log && (
                                    <div className="override-log">
                                      Override: &quot;{log.reason}&quot;
                                      {log.at && ` · ${new Date(log.at).toLocaleDateString()}`}
                                    </div>
                                  )}
                                </div>
                                <div className="assumption-right">
                                  {row.key && !readOnly ? (
                                    <>
                                      <button
                                        type="button"
                                        className={`assumption-val${overridden ? ' overridden' : ''}`}
                                        onClick={() => {
                                          setOverrideModal({
                                            key: row.key,
                                            label: row.label,
                                            current: val,
                                            defaultFmt: row.defaultFmt,
                                          });
                                          setOverrideDraft({
                                            value: String(overrides[row.key] ?? defaultOverrideValue(row.key) ?? ''),
                                            reason: '',
                                          });
                                        }}
                                      >
                                        {val}
                                      </button>
                                      {overridden && (
                                        <button type="button" className="assumption-reset" onClick={() => resetOverride(row.key)}>
                                          Reset
                                        </button>
                                      )}
                                    </>
                                  ) : (
                                    <span style={{ fontSize: 11.5, color: '#2c2c2a', fontWeight: 500 }}>{val}</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {overrideModal && (
        <div className="override-modal show">
          <div className="override-box">
            <div className="override-title">Override: {overrideModal.label}</div>
            <div className="override-subtitle">
              Current: {overrideModal.current} · Platform default: {overrideModal.defaultFmt}
            </div>
            <input
              className="override-input"
              value={overrideDraft.value}
              onChange={(e) => setOverrideDraft((p) => ({ ...p, value: e.target.value }))}
            />
            <textarea
              className="override-reason"
              rows={2}
              placeholder="Reason for override (required)…"
              value={overrideDraft.reason}
              onChange={(e) => setOverrideDraft((p) => ({ ...p, reason: e.target.value }))}
            />
            <div className="override-actions">
              <button type="button" className="btn-ghost" onClick={() => setOverrideModal(null)}>Cancel</button>
              <button type="button" className="btn-ghost" onClick={() => resetOverride(overrideModal.key)}>Reset to default</button>
              <button type="button" className="btn-primary" disabled={saving} onClick={applyOverride}>Apply override</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={toast.type === 'success' ? 'toast-success' : 'toast-error'}>{toast.msg}</div>
      )}
    </InitiationLayout>
  );
}
